import { Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FormControlsOf } from '@y42/angular/forms';
import { ShareWith, Y42AssetStatus, Y42Metadata } from '@y42/json-schema';
import { debounceTime, filter, tap } from 'rxjs';
import { AssetSidebarService } from './asset-sidebar.service';

export type Y42MetadataForm = FormControlsOf<Omit<Y42Metadata, '$schema'>>;

@UntilDestroy()
@Injectable()
export class AssetSidebarFormService {
  constructor(private readonly assetSidebarService: AssetSidebarService) {
    this.form.valueChanges
      .pipe(
        // since git operations are Promise-based and therefore not cancelable
        // we should allow for a little more debounce
        debounceTime(1000),
        filter(() => this.form.valid && this.form.dirty),
        tap((asset) => this.assetSidebarService.updateActive(asset)),
        untilDestroyed(this),
      )
      .subscribe();

    this.assetSidebarService.asset$
      .pipe(
        tap((asset) => {
          const formValue = this.form.value as Y42Metadata;
          if (!asset) {
            return;
          }

          // calling `patchValue` on quill-editor form controls resets the text cursor positon
          // so we patch only those form fields that have actually changed

          const delta = Object.entries(asset)
            .filter(([key, value]) => formValue[key as keyof Y42Metadata] !== value)
            .reduce((obj, [key, value]) => ({ ...obj, [key]: value }), {} as Partial<Y42Metadata>);

          this.form.patchValue(delta, { emitEvent: false });
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  readonly form = new FormGroup<Y42MetadataForm>({
    name: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(2), Validators.maxLength(90)],
    }),
    description: new FormControl('', { nonNullable: true }),
    tags: new FormControl([], { nonNullable: true }),
    owners: new FormControl<ShareWith>(
      {
        user_ids: [],
        team_ids: [],
      },
      { nonNullable: true },
    ),
    watchers: new FormControl<ShareWith>(
      {
        user_ids: [],
        team_ids: [],
      },
      { nonNullable: true },
    ),
    experts: new FormControl<ShareWith>(
      {
        user_ids: [],
        team_ids: [],
      },
      { nonNullable: true },
    ),
    asset_status: new FormControl(Y42AssetStatus.NoStatus, { nonNullable: true }),
    asset_status_message: new FormControl(undefined, { nonNullable: true }),
  });
}
